import PaymentProcessingModal from "../../modal/PaymentProcessingModal";
import TransactionStatusModal from "../../modal/TransactionStatusModal";
import {formatCardNumber } from "../../../utils/pagbrasilStyleMapper";

const PagbrasilCardFormView = ({
  t,
  siteName,
  // Form data
  cardholderName,
  cardNumber,
  expirationDate,
  cvv,
  documentNumber,
  installments,
  // State
  loading,
  error,
  cardError,
  sdkLoading,
  sdkReady,
  cardBrand,
  // Handlers
  onCardholderNameChange,
  onCardNumberChange,
  onExpirationDateChange,
  onCvvChange,
  onDocumentChange,
  onInstallmentsChange,
  // Input styles
  inputStyles,
  labelStyles,
  // Modals
  showProcessingModal,
  setProcessingModal,
  showStatusModal,
  setShowStatusModal,
  transactionStatus,
  checkoutConfDto,
  transactionId,
  hostedPageRedirect,
  // Options
  showInstallments = false,
  maxInstallments = 12,
}) => {
  // Generate installment options
  const installmentOptions = Array.from({ length: maxInstallments }, (_, i) => i + 1);

  return (
    <div className="card-container">
      {/* Cardholder Name */}
      <div className="form-section">
        <label className="input-label" style={labelStyles}>
          {t("paymentForms.card.cardholderName")}
        </label>
        <div className="input-wrapper">
          <input
            type="text"
            className="card-input"
            style={inputStyles}
            placeholder={t("paymentForms.card.namePlaceholder")}
            value={cardholderName}
            onChange={onCardholderNameChange}
            disabled={loading}
            autoComplete="cc-name"
            PB_cc_holder="true"
          />
        </div>
      </div>

      {/* Card Number */}
      <div className="form-section">
        <label className="input-label" style={labelStyles}>
          {t("paymentForms.card.cardNumber")}
          {cardBrand && (
            <span className="card-brand-badge">{cardBrand}</span>
          )}
        </label>
        <div className="input-wrapper">
          <input
            type="text"
            className="card-input"
            style={inputStyles}
            placeholder={t("paymentForms.pagbrasilCard.cardNumberPlaceholder") || "0000 0000 0000 0000"}
            value={formatCardNumber(cardNumber)}
            onChange={onCardNumberChange}
            disabled={loading}
            autoComplete="cc-number"
            maxLength={19}
            inputMode="numeric"
            PB_cc_number="true"
          />
        </div>
      </div>

      {/* Expiration Date and CVV Row */}
      <div className="form-row" style={{ marginBottom: "1rem" }}>
        <div className="form-section half-width">
          <label className="input-label" style={labelStyles}>
            {t("paymentForms.pagbrasilCard.expirationDate") || "Expiration Date"}
          </label>
          <div className="input-wrapper">
            <input
              type="text"
              className="card-input"
              style={inputStyles}
              placeholder="MM/YY"
              value={expirationDate}
              onChange={onExpirationDateChange}
              disabled={loading}
              autoComplete="cc-exp"
              maxLength={5}
              inputMode="numeric"
              PB_cc_expiration="true"
            />
          </div>
        </div>

        <div className="form-section half-width">
          <label className="input-label" style={labelStyles}>
            {t("paymentForms.pagbrasilCard.cvv") || "CVV"}
          </label>
          <div className="input-wrapper">
            <input
              type="text"
              className="card-input"
              style={inputStyles}
              placeholder="123"
              value={cvv}
              onChange={onCvvChange}
              disabled={loading}
              autoComplete="cc-csc"
              maxLength={4}
              inputMode="numeric"
              PB_cc_cvv="true"
            />
          </div>
        </div>
      </div>

      {/* CPF/CNPJ Document */}
      <div className="form-section">
        <label className="input-label" style={labelStyles}>
          {t("paymentForms.pix.document")}
        </label>
        <div className="input-wrapper">
          <input
            type="text"
            className="card-input"
            style={inputStyles}
            placeholder={t("paymentForms.pix.documentPlaceholder")}
            value={documentNumber}
            onChange={onDocumentChange}
            disabled={loading}
            maxLength={14}
          />
        </div>
      </div>

      {/* Installments (optional) */}
      {showInstallments && (
        <div className="form-section">
          <label className="input-label" style={labelStyles}>
            {t("paymentForms.pagbrasilCard.installments") || "Installments"}
          </label>
          <div className="input-wrapper">
            <select
              className="card-input"
              style={inputStyles}
              value={installments}
              onChange={onInstallmentsChange}
              disabled={loading}
              PB_cc_installments="true"
            >
              {installmentOptions.map((num) => (
                <option key={num} value={num}>
                  {num}x {num === 1 
                    ? (t("paymentForms.pagbrasilCard.noInstallments") || "à vista") 
                    : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {siteName && (
        <p className="input-hint">
          {t("paymentForms.pagbrasilCard.statementDescriptor", {
            variables: { siteName },
          })}
        </p>
      )}

      {/* SDK Loading State */}
      {sdkLoading && (
        <div className="sdk-loading-text">
          {t("paymentForms.card.loadingSDK") || "Loading payment form..."}
        </div>
      )}

      {/* Card Error */}
      {cardError && <div className="error-message">{cardError}</div>}

      {/* General Error */}
      {error && !cardError && <div className="error-message">{error}</div>}

      {/* Loading Overlay */}
      {loading && (
        <div className="loader-overlay">
          <div className="spinner-large" />
          <div className="loading-text">
            {t("paymentForms.card.processing") || "Processing payment..."}
          </div>
        </div>
      )}

      <PaymentProcessingModal
        show={showProcessingModal}
        setShow={setProcessingModal}
      />

      <TransactionStatusModal
        show={showStatusModal}
        setShow={setShowStatusModal}
        status={transactionStatus}
        transactionId={transactionId}
        hostedPageRedirect={hostedPageRedirect}
        fallbackRedirectUrl={checkoutConfDto?.checkoutRedirectUrl}
      />
    </div>
  );
};

export default PagbrasilCardFormView;
